/**
 * pages/Procedimientos.js
 * Vista de usuario - Procedimientos asignados
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { FaFileAlt, FaDownload, FaCheck, FaClock, FaEye } from 'react-icons/fa';
import CustomNavbar from '../components/Navbar';
import ProcedimientoVisor from '../components/ProcedimientoVisor';
import { toast } from 'react-toastify';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const Procedimientos = () => {
  const [procedimientos, setProcedimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [visorOpen, setVisorOpen] = useState(false);
  const [procedimientoActual, setProcedimientoActual] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUsuario(JSON.parse(storedUser));
    }
    fetchProcedimientos();
  }, []);

  const fetchProcedimientos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procedimientos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar procedimientos');
      }

      const data = await response.json();
      setProcedimientos(data.data || []);
    } catch (err) {
      console.error('Error fetching procedimientos:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerProcedimiento = (proc) => {
    setProcedimientoActual(proc);
    setVisorOpen(true);
  };

  const handleDescargar = async (proc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/procedimientos/${proc.id}/file?token=${token}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Error al descargar');

      // Convertir a blob y forzar descarga
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = proc.archivo_nombre || `procedimiento_${proc.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error('Error al descargar');
      console.error(err);
    }
  };

  const handleConfirmar = async (proc) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/procedimientos/${proc.id}/confirm`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Mostrar error específico según el código de error
        if (data.errorCode === 'NOT_OPENED') {
          toast.error('📂 ' + data.error);
        } else if (data.errorCode === 'MIN_TIME_NOT_MET') {
          toast.warning(`⏱️ ${data.error}`);
        } else if (data.errorCode === 'SCROLL_NOT_COMPLETED') {
          toast.warning('📜 ' + data.error);
        } else if (data.segundosFaltantes) {
          toast.warning(`⏱️ ${data.error}`);
        } else {
          toast.error(data.error || 'Error al confirmar');
        }
        
        // Log para debugging
        console.error('Error al confirmar:', {
          status: response.status,
          errorCode: data.errorCode,
          error: data.error,
          procedimiento: proc.id
        });
        
        return;
      }

      toast.success('✅ Procedimiento confirmado correctamente');
      fetchProcedimientos(); // Refrescar lista
    } catch (err) {
      console.error('Error al confirmar:', err);
      toast.error('Error al confirmar procedimiento');
    }
  };

  const handleVisorClose = () => {
    setVisorOpen(false);
    setProcedimientoActual(null);
    fetchProcedimientos(); // Refrescar para actualizar estados
  };

  const renderEstadoBadge = (proc) => {
    if (proc.status === 'confirmado') {
      return <Badge bg="success"><FaCheck /> Confirmado</Badge>;
    }
    if (proc.opened_at) {
      return <Badge bg="warning"><FaClock /> En proceso</Badge>;
    }
    return <Badge bg="secondary">Pendiente</Badge>;
  };



  if (loading) {
    return (
      <div className="loading-container">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Cargando procedimientos...</p>
      </div>
    );
  }

  return (
    <>
      <CustomNavbar userData={usuario} />
      <Container className="py-4">
        <Row className="mb-4">
          <Col>
            <h2><FaFileAlt /> Procedimientos</h2>
            <p className="text-muted">
              Revisá y confirmá los procedimientos asignados a tu rol
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        {procedimientos.length === 0 ? (
          <Alert variant="info">
            No tenés procedimientos pendientes en este momento
          </Alert>
        ) : (
          <Row>
            {procedimientos.map(proc => (
              <Col key={proc.id} xs={12} md={6} lg={4} className="mb-4">
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="mb-0">{proc.titulo}</h5>
                      {renderEstadoBadge(proc)}
                    </div>
                    
                    <p className="text-muted small mb-2">
                      Versión {proc.version}
                    </p>

                    {proc.descripcion && (
                      <p className="mb-3">{proc.descripcion}</p>
                    )}

                    {/* Info de tracking */}
                    <div className="mb-3">
                      {proc.opened_at && (
                        <small className="text-muted d-block">
                          <FaEye /> Abierto: {new Date(proc.opened_at).toLocaleString('es-AR')}
                        </small>
                      )}
                      {proc.confirmed_at && (
                        <small className="text-success d-block">
                          <FaCheck /> Confirmado: {new Date(proc.confirmed_at).toLocaleString('es-AR')}
                        </small>
                      )}
                    </div>

                    {/* Botones de acción */}
                    <div className="d-grid gap-2">
                      {!proc.confirmed_at && (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleVerProcedimiento(proc)}
                          >
                            <FaEye /> Ver Procedimiento
                          </Button>
                          
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleConfirmar(proc)}
                            disabled={!proc.opened_at}
                          >
                            <FaCheck /> Confirmar Lectura
                          </Button>
                        </>
                      )}

                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleDescargar(proc)}
                      >
                        <FaDownload /> Descargar
                      </Button>
                    </div>

                    {/* Advertencias */}
                    {proc.requiere_scroll_fin === 1 && !proc.scroll_completed_at && (
                      <small className="text-warning d-block mt-2">
                        ⚠️ Debés leer todo el documento
                      </small>
                    )}
                    {proc.min_seconds > 0 && !proc.confirmed_at && (
                      <small className="text-info d-block mt-1">
                        ℹ️ Tiempo mínimo: {proc.min_seconds} segundos
                      </small>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>

      {/* Modal Visor */}
      {visorOpen && procedimientoActual && (
        <ProcedimientoVisor
          show={visorOpen}
          onHide={handleVisorClose}
          procedimiento={procedimientoActual}
        />
      )}
    </>
  );
};

export default Procedimientos;
